"use client";

interface BreakdownRow {
  label: string;
  value: number;
}

interface BreakdownTableProps {
  rows: BreakdownRow[];
  total?: number;
  currencySymbol?: string;
  locale?: string;
}

export default function BreakdownTable({
  rows,
  total,
  currencySymbol = "$",
  locale = "en-US",
}: BreakdownTableProps) {
  if (!rows || rows.length === 0) {
    return <div className="empty-table-state">No data available</div>;
  }

  const calculatedTotal = total ?? rows.reduce((sum, r) => sum + Math.max(0, r.value), 0);

  const formatVal = (val: number) => {
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(val));

    return val < 0 ? `-${currencySymbol}${formatted}` : `${currencySymbol}${formatted}`;
  };

  return (
    <div className="table-wrapper anim-fade-in">
      <table className="breakdown-table">
        <thead>
          <tr>
            <th className="th-label">Category</th>
            <th className="th-bar">Breakdown</th>
            <th className="th-value text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const barPercent = Math.min(100, Math.max(0, calculatedTotal > 0 ? (Math.max(0, row.value) / calculatedTotal) * 100 : 0));
            
            return (
              <tr key={index} className="table-row">
                <td className="td-label font-semibold">{row.label}</td>
                <td className="td-bar">
                  <div className="share-bar-track">
                    <div
                      className="share-bar-fill"
                      style={{ width: `${barPercent}%` }}
                    />
                  </div>
                </td>
                <td className={`td-value text-right font-bold ${row.value < 0 ? "negative-value" : ""}`}>
                  {formatVal(row.value)}
                </td>
              </tr>
            );
          })}

          {total !== undefined && (
            <tr className="table-row-total">
              <td className="td-label font-bold">Total</td>
              <td className="td-bar"></td>
              <td className={`td-value text-right font-bold ${total < 0 ? "negative-value" : ""}`}>
                {formatVal(total)}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <style jsx>{`
        .table-wrapper {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          margin-top: var(--space-4);
          margin-bottom: var(--space-4);
          border-radius: var(--radius-md);
          border: 1px solid var(--border-color);
          box-sizing: border-box;
        }
        .breakdown-table {
          width: 100%;
          min-width: 0;
          border-collapse: collapse;
          text-align: left;
          font-family: var(--font-body);
        }
        th {
          background-color: var(--bg-surface-hover);
          color: var(--text-muted);
          font-family: var(--font-heading);
          font-size: var(--text-xs);
          font-weight: var(--fw-extrabold);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: var(--space-3) var(--space-4);
          border-bottom: 1px solid var(--border-color);
        }
        .th-label { width: 40%; }
        .th-bar { width: 40%; }
        .th-value { width: 20%; min-width: 80px; }
        @media (max-width: 480px) {
          .th-bar, .td-bar { display: none; }
          .th-label { width: 65%; }
          .th-value { width: 35%; }
          th, td { padding: var(--space-2) var(--space-3); font-size: var(--text-sm); }
        }
        
        td {
          padding: var(--space-3) var(--space-4);
          border-bottom: 1px solid var(--border-color);
          vertical-align: middle;
          font-size: var(--text-sm);
          color: var(--text-primary);
        }
        .table-row:nth-child(even) {
          background-color: transparent;
        }
        .table-row:hover {
          background-color: var(--bg-surface-hover);
        }
        .td-label {
          word-break: break-word;
          overflow-wrap: break-word;
          max-width: 0;
          min-width: 120px;
        }
        .share-bar-track {
          height: 6px;
          background-color: var(--border-color);
          border-radius: var(--radius-full);
          width: 100%;
          overflow: hidden;
        }
        .share-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--secondary) 0%, var(--primary) 100%);
          border-radius: var(--radius-full);
        }
        .negative-value {
          color: var(--danger) !important;
        }
        .table-row-total {
          background-color: var(--bg-surface-hover) !important;
        }
        .table-row-total td {
          border-bottom: none;
          color: var(--text-primary);
          font-weight: var(--fw-extrabold);
        }
        .empty-table-state {
          padding: var(--space-8);
          text-align: center;
          color: var(--text-muted);
          font-style: italic;
          border: 1px dashed var(--border-color);
          border-radius: var(--radius-md);
        }
      `}</style>
    </div>
  );
}
export type { BreakdownRow };
